import { GetStorageHandlerPayload } from '@hqo/hqo-miniapp-client-sdk';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useMiniappSdk, useMiniappSdkEventHandler } from 'components/miniapp-sdk-provider/miniapp-sdk.hook';
import { selectBuildingUuid } from 'store/building/selectors';
import { useHostInfo } from 'store/host-info/hooks/use-host-info';
import { hydrateSelectedBooking } from 'store/selected-booking/actions';
import { SELECTED_BOOKING_STORAGE_KEY } from 'store/selected-booking/consts';
import { readStoredSelection } from 'store/selected-booking/utils';

/**
 * Brings the host's stored booking selection into the store, once, on boot.
 *
 * The host seeds that entry from a url naming a booking and when a booking tile is tapped on its
 * own surfaces, so this is how a cold landing -- including a RELOAD, where the host url is the bare
 * list and carries nothing -- finds out which booking to open.
 *
 * Mounted in `app`, and only there. `sdk.on` has no counterpart: the SDK pushes handlers onto an
 * array and offers no way to remove one, and `useMiniappSdkEventHandler` registers without a
 * cleanup. So a listener in a component that remounts accumulates a copy per mount, every one of
 * which fires. Registered once with stable dependencies, in something that mounts once, that cannot
 * happen -- which is also why the handler reads the building through a ref rather than closing over
 * it.
 *
 * ⛔ ASKED ONLY WHERE THE ANSWER IS WANTED. The entry is written by the tenant portal and restored
 * for the tenant portal (see `useSelectedBooking`), so on any other host this is a question about a
 * key nothing wrote. The listener stays registered regardless -- it has to, since registration is
 * once-and-for-all at app mount, before the host has said what it is -- and simply never hears an
 * answer to a question that was never asked.
 */
export const useSyncSelectedBookingWithHostApp = (): void => {
  const client = useMiniappSdk();
  const dispatch = useDispatch();
  const buildingUuid = useSelector(selectBuildingUuid);
  const { isTenantPortalHost } = useHostInfo();

  /*
   * The handler reads the building through a ref rather than closing over it: closing over it would
   * freeze the value this effect first saw, and the dependency array that would otherwise fix that
   * is exactly what must stay empty here (see above).
   *
   * The ref is not what makes the building available -- the request below waits for it. A ref only
   * helps once a value exists; it cannot conjure one for an answer that arrives first.
   */
  const buildingUuidRef = useRef(buildingUuid);
  buildingUuidRef.current = buildingUuid;

  useMiniappSdkEventHandler(
    'getStorageResponse',
    (data: GetStorageHandlerPayload) => {
      // One listener for every key the app ever reads; anything else is somebody else's answer.
      if (!data || !(SELECTED_BOOKING_STORAGE_KEY in data)) return;

      // Typed as a string, but the host hands back `localStorage.getItem`, which is null for a key
      // that was never written.
      const raw = data[SELECTED_BOOKING_STORAGE_KEY] as string | null;

      /*
       * ⚠️ HYDRATION, NOT A SELECTION. This answer is a postMessage round trip behind the
       * question, and the tenant can open another booking or press Back inside that window. The
       * reducer drops it if they have -- see `hasLocalSelection`.
       */
      dispatch(hydrateSelectedBooking(readStoredSelection(raw, buildingUuidRef.current)));
    },
    [],
  );

  const hasAskedRef = useRef(false);

  useEffect(() => {
    /*
     * Asked once per load. The entry answers "what was open when this frame was last built", which
     * is a question with one moment -- re-asking later would hand back a value this app has since
     * moved past and reopen it under the tenant.
     */
    /*
     * Waits for the BUILDING, not just the client.
     *
     * The entry is scoped to a building and `readStoredSelection` discards one it cannot scope, so
     * asking before the building is known throws the answer away. That is not a narrow race: the
     * client is ready early and the answer is a postMessage round trip, while the building is a
     * network fetch -- the answer usually wins. And it is unrecoverable, because the guard below
     * means the question is never asked again.
     */
    if (!isTenantPortalHost || !client || !buildingUuid || hasAskedRef.current) return;

    hasAskedRef.current = true;
    client.storage.get(SELECTED_BOOKING_STORAGE_KEY);
  }, [isTenantPortalHost, client, buildingUuid]);
};
